/**
 * 统一数据字典管理器
 * 负责数据字典的获取、存储、缓存和按需加载
 */

import { DICT_TYPES } from '../constants/dictTypes';

// 导入DictItem类型
export interface DictItem {
  id: number;
  dictLabel: string;
  dictValue: string;
  dictSort: number;
  status: number;
  cssClass?: string;
  listClass?: string;
  isDefault?: string;
  remark?: string;
}

export interface DictCache {
  data: DictItem[];
  timestamp: number;
  version: string;
}

// 存储配置
const STORAGE_CONFIG = {
  PREFIX: 'dict_cache_',
  VERSION: '1.0.0',
  EXPIRY_TIME: 30 * 24 * 60 * 60 * 1000, // 30天过期（字典数据变化不频繁，延长缓存时间）
};

class UnifiedDictManager {
  private static instance: UnifiedDictManager;
  private memoryCache: Map<string, DictCache> = new Map();
  private loadingPromises: Map<string, Promise<any>> = new Map();
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): UnifiedDictManager {
    if (!UnifiedDictManager.instance) {
      UnifiedDictManager.instance = new UnifiedDictManager();
    }
    return UnifiedDictManager.instance;
  }

  /**
   * 初始化管理器
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // 从localStorage加载所有缓存的字典数据
      await this.loadFromStorage();
      this.isInitialized = true;
    } catch (error) {
      console.error('统一数据字典管理器初始化失败:', error);
      throw error;
    }
  }

  /**
   * 从localStorage加载所有缓存的字典数据
   */
  private async loadFromStorage(): Promise<void> {
    try {
      const allDictTypes = Object.values(DICT_TYPES);
      
      for (const dictType of allDictTypes) {
        const cacheKey = this.getStorageKey(dictType);
        const cachedData = localStorage.getItem(cacheKey);
        
        if (cachedData) {
          try {
            const cache: DictCache = JSON.parse(cachedData);
            
            // 无论缓存是否过期，都加载到内存
            // 字典数据变化不频繁，即使过期也优先使用缓存，避免频繁的API调用
            this.memoryCache.set(dictType, cache);
          } catch (error) {
            localStorage.removeItem(cacheKey);
          }
        }
      }
      
    } catch (error) {
      console.error('从storage加载字典缓存失败:', error);
    }
  }

  /**
   * 批量预加载所有字典数据（登录成功后调用）
   */
  public async preloadAllDicts(): Promise<void> {
    try {
      
      // 检查是否已经有批量加载正在进行
      const batchKey = 'batch_preload_all';
      const existingBatchPromise = this.loadingPromises.get(batchKey);
      if (existingBatchPromise) {
        await existingBatchPromise;
        return;
      }

      const batchPromise = this.doBatchPreload();
      this.loadingPromises.set(batchKey, batchPromise);

      try {
        await batchPromise;
      } finally {
        this.loadingPromises.delete(batchKey);
      }
    } catch (error) {
      console.error('批量预加载所有字典数据失败:', error);
      throw error;
    }
  }

  /**
   * 执行批量预加载
   */
  private async doBatchPreload(): Promise<void> {
    try {
      const { dictDataService } = await import('../services/dictService');
      const allDictTypes = Object.values(DICT_TYPES);
      
      
      // 使用批量API一次性获取所有字典数据
      const response = await dictDataService.getBatchByTypes(allDictTypes);
      
      if (response.code === 200) {
        const batchData = response.data;
        let loadedCount = 0;
        
        // 将批量数据分别缓存到内存和storage
        for (const [dictType, data] of Object.entries(batchData)) {
          const dictItems = data as DictItem[];
          await this.setDictData(dictType, dictItems);
          loadedCount++;
        }
        
      } else {
        throw new Error(response.message || '批量获取字典数据失败');
      }
    } catch (error) {
      console.error('批量预加载失败:', error);
      throw error;
    }
  }

  /**
   * 获取字典数据（优先从缓存读取，只在缓存完全不存在时才从API获取）
   * 字典数据应该在登录时一次性全部加载到本地，这里主要从缓存读取
   * 即使缓存过期也优先使用缓存，避免频繁的API调用
   */
  public async getDictData(dictType: string): Promise<DictItem[]> {
    // 1. 检查内存缓存（即使过期也使用，避免频繁API调用）
    const memoryCache = this.memoryCache.get(dictType);
    if (memoryCache) {
      return memoryCache.data;
    }

    // 2. 检查storage缓存（即使过期也使用，避免频繁API调用）
    const storageCache = this.getFromStorage(dictType);
    if (storageCache) {
      // 更新内存缓存
      this.memoryCache.set(dictType, storageCache);
      return storageCache.data;
    }

    // 3. 检查是否正在加载中（避免重复请求）
    const existingPromise = this.loadingPromises.get(dictType);
    if (existingPromise) {
      return await existingPromise;
    }

    // 4. 缓存完全不存在时，才从API获取（这种情况应该很少，因为字典在登录时已经全部加载）
    // 只有在登录后首次访问且localStorage中没有缓存时才会触发API调用
    const promise = this.fetchFromAPI(dictType);
    this.loadingPromises.set(dictType, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      this.loadingPromises.delete(dictType);
    }
  }

  /**
   * 从API获取字典数据
   */
  private async fetchFromAPI(dictType: string): Promise<DictItem[]> {
    try {
      const { dictDataService } = await import('../services/dictService');
      
      // 使用单个字典API获取数据
      const response = await dictDataService.getAllByType(dictType);

      if (response.code === 200) {
        const data = (response.data || []).sort((a: any, b: any) => (a.dictSort || 0) - (b.dictSort || 0));
        
        // 缓存到内存和storage
        await this.setDictData(dictType, data);
        
        return data;
      } else {
        throw new Error(response.message || '获取字典数据失败');
      }
    } catch (error) {
      console.error(`从API获取字典数据失败: ${dictType}`, error);
      throw error;
    }
  }

  /**
   * 设置字典数据到缓存
   */
  public async setDictData(dictType: string, data: DictItem[]): Promise<void> {
    const cache: DictCache = {
      data,
      timestamp: Date.now(),
      version: STORAGE_CONFIG.VERSION,
    };

    // 更新内存缓存
    this.memoryCache.set(dictType, cache);

    // 更新storage缓存
    try {
      const cacheKey = this.getStorageKey(dictType);
      localStorage.setItem(cacheKey, JSON.stringify(cache));
    } catch (error) {
    }
  }

  /**
   * 从storage获取缓存
   */
  private getFromStorage(dictType: string): DictCache | null {
    try {
      const cacheKey = this.getStorageKey(dictType);
      const cacheData = localStorage.getItem(cacheKey);
      if (cacheData) {
        return JSON.parse(cacheData);
      }
    } catch (error) {
    }
    return null;
  }

  /**
   * 获取storage键名
   */
  private getStorageKey(dictType: string): string {
    return `${STORAGE_CONFIG.PREFIX}${dictType}`;
  }

  /**
   * 检查缓存是否过期
   */
  private isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > STORAGE_CONFIG.EXPIRY_TIME;
  }

  /**
   * 清除所有字典缓存
   */
  public clearAllCache(): void {
    // 清除内存缓存
    this.memoryCache.clear();

    // 清除storage缓存
    const allDictTypes = Object.values(DICT_TYPES);
    for (const dictType of allDictTypes) {
      const cacheKey = this.getStorageKey(dictType);
      localStorage.removeItem(cacheKey);
    }

  }

  /**
   * 清除指定字典类型的缓存
   */
  public clearDictCache(dictType: string): void {
    // 清除内存缓存
    this.memoryCache.delete(dictType);

    // 清除storage缓存
    const cacheKey = this.getStorageKey(dictType);
    localStorage.removeItem(cacheKey);

  }

  /**
   * 获取缓存状态信息
   */
  public getCacheStatus(): { [key: string]: { cached: boolean; count: number; expired: boolean } } {
    const status: { [key: string]: { cached: boolean; count: number; expired: boolean } } = {};
    const allDictTypes = Object.values(DICT_TYPES);

    for (const dictType of allDictTypes) {
      const cache = this.memoryCache.get(dictType);
      status[dictType] = {
        cached: !!cache,
        count: cache?.data.length || 0,
        expired: cache ? this.isExpired(cache.timestamp) : false,
      };
    }

    return status;
  }

  /**
   * 初始化所有字典数据
   * 在登录成功后调用，一次性加载所有字典数据
   */
  public async initializeAllDicts(): Promise<void> {
    try {
      const { dictService } = await import('../services/dictService');
      const response = await dictService.getAllEnabledDictData();
      
      if (response.code === 200) {
        const allData = response.data || [];
        
        const groupedData = allData.reduce((acc: { [key: string]: DictItem[] }, item: any) => {
          const dictType = item.dictType;
          if (!acc[dictType]) {
            acc[dictType] = [];
          }
          const convertedItem: DictItem = {
            id: item.dictCode,
            dictLabel: item.dictLabel,
            dictValue: item.dictValue,
            dictSort: item.dictSort,
            status: item.status,
            cssClass: item.cssClass,
            listClass: item.listClass,
            isDefault: item.isDefault,
            remark: item.remark
          };
          acc[dictType].push(convertedItem);
          return acc;
        }, {});
        
        const timestamp = Date.now();
        for (const [dictType, data] of Object.entries(groupedData)) {
          const sortedData = (data as DictItem[]).sort((a, b) => (a.dictSort || 0) - (b.dictSort || 0));
          this.memoryCache.set(dictType, {
            data: sortedData,
            timestamp,
            version: STORAGE_CONFIG.VERSION,
          });
          await this.setDictData(dictType, sortedData); // Also cache to local storage
        }
        this.isInitialized = true;
      } else {
        throw new Error(response.message || '初始化字典数据失败');
      }
    } catch (error) {
      console.error('初始化字典数据失败:', error);
      throw error;
    }
  }

  /**
   * 检查是否已初始化
   */
  public isReady(): boolean {
    return this.isInitialized;
  }
}

// 导出单例实例
export const unifiedDictManager = UnifiedDictManager.getInstance();
export default unifiedDictManager;
